import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { type ErrorNotification } from "@via/utils/interfaces.tsx";
import type {
  EventDefinitionEdit,
  EventDefinitionInput,
  EventDefinitionResponseBody,
  EventDefinitionUpdateBody,
  EventDeleteResponseBody,
  EventEditorOptions,
  EventEditorOptionsResponseBody,
  EventListItem,
  EventPermsRequestBody,
  EventPermsResponseBody,
  EventShareItem,
  EventSharingListResponseBody,
  EventListResponseBody,
  EventSubmitRequestBody,
  EventSubmitResponseBody,
  EventSubmitResult,
  EventView,
  EventViewResponseBody,
} from "common";

export const VMETA_EVENTS_KEY = "/api/v1/vmeta/canvas/events";
/**
 * Whether an id may become one path segment. The router has already decoded the address bar,
 * and "." or ".." stay dot segments even when encoded, so the URL parser would drop them.
 */
export const isSafeSegment = (id: string | undefined | null): id is string =>
  !!id && id !== "." && id !== "..";

// Ids can come from the browser address bar, so every one is checked and encoded.
const segment = (id: string): string => {
  if (!isSafeSegment(id)) throw new Error("Invalid id.");
  return encodeURIComponent(id);
};

const base = (canvasId: string) =>
  `/api/v1/vmeta/canvas/${segment(canvasId)}/events`;

/** The server's fixed message (a refusal reason, 409), or the fallback. */
export const eventErrorMessage = (error: unknown, fallback: string): string =>
  (error as ErrorNotification)?.response?.data?.message || fallback;

export const eventErrorStatus = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

export const useVmetaEvents = (
  canvasId: string,
  enabled: boolean
): UseQueryResult<EventListItem[], Error> =>
  useQuery({
    queryKey: [VMETA_EVENTS_KEY, canvasId],
    queryFn: async () =>
      (await axios.get<EventListResponseBody>(base(canvasId))).data.data,
    enabled: enabled && isSafeSegment(canvasId),
    retry: false,
  });

export const useVmetaEvent = (
  canvasId: string,
  eventId: string | undefined
): UseQueryResult<EventView, Error> =>
  useQuery({
    queryKey: [VMETA_EVENTS_KEY, canvasId, eventId],
    queryFn: async () =>
      (
        await axios.get<EventViewResponseBody>(
          `${base(canvasId)}/${segment(eventId ?? "")}`
        )
      ).data.data,
    enabled: isSafeSegment(canvasId) && isSafeSegment(eventId),
    retry: false,
  });

export const useSubmitVmetaEvent = (): UseMutationResult<
  EventSubmitResult[],
  unknown,
  { canvasId: string; eventId: string; body: EventSubmitRequestBody }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ canvasId, eventId, body }) =>
      (
        await axios.post<EventSubmitResponseBody>(
          `${base(canvasId)}/${segment(eventId)}/submit`,
          body
        )
      ).data.data.results,
    gcTime: 0,
    // Record lists elsewhere in the tracker show the new rows, and the event
    // pickers (Task 4) refetch their candidates.
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["/api/v1/vmeta/canvas"] }),
        queryClient.invalidateQueries({ queryKey: ["event-picker"] }),
        queryClient.invalidateQueries({ queryKey: ["event-picker-record"] }),
      ]);
    },
  });
};

export const useVmetaEventSharing = (
  canvasId: string,
  enabled: boolean
): UseQueryResult<EventShareItem[], Error> =>
  useQuery({
    // "sharing" can never collide with the event id slot of useVmetaEvent's key.
    queryKey: [VMETA_EVENTS_KEY, canvasId, "sharing"],
    queryFn: async () =>
      (
        await axios.get<EventSharingListResponseBody>(
          `${base(canvasId)}/sharing`
        )
      ).data.data,
    enabled: enabled && isSafeSegment(canvasId),
    retry: false,
  });

export const useUpdateVmetaEventPerms = (): UseMutationResult<
  EventShareItem,
  unknown,
  { canvasId: string; eventId: string; body: EventPermsRequestBody }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ canvasId, eventId, body }) =>
      (
        await axios.put<EventPermsResponseBody>(
          `${base(canvasId)}/${segment(eventId)}/perms`,
          body
        )
      ).data.data,
    // Success or a 409: the list, the runner list and any open form refetch, so the dialog
    // and the runner never keep a version the server no longer has.
    onSettled: async (_data, _error, { canvasId }) => {
      await queryClient.invalidateQueries({
        queryKey: [VMETA_EVENTS_KEY, canvasId],
      });
    },
  });
};

export const useVmetaEventEditorOptions = (
  canvasId: string,
  enabled: boolean
): UseQueryResult<EventEditorOptions, Error> =>
  useQuery({
    // "editor-options" can never collide with the event id slot of useVmetaEvent's key.
    queryKey: [VMETA_EVENTS_KEY, canvasId, "editor-options"],
    queryFn: async () =>
      (
        await axios.get<EventEditorOptionsResponseBody>(
          `${base(canvasId)}/editor-options`
        )
      ).data.data,
    enabled: enabled && isSafeSegment(canvasId),
    retry: false,
  });

export const useVmetaEventDefinition = (
  canvasId: string,
  eventId: string | undefined
): UseQueryResult<EventDefinitionEdit, Error> =>
  useQuery({
    queryKey: [VMETA_EVENTS_KEY, canvasId, eventId, "definition"],
    queryFn: async () =>
      (
        await axios.get<EventDefinitionResponseBody>(
          `${base(canvasId)}/${segment(eventId ?? "")}/definition`
        )
      ).data.data,
    enabled: isSafeSegment(canvasId) && isSafeSegment(eventId),
    // Every open of the editor reads the stored definition afresh.
    gcTime: 0,
    retry: false,
  });

// Success or a 409, the three editor mutations refetch the list, the sharing view and any
// open form or definition, so nothing keeps a version the server no longer has. The drawer
// must not let that background refetch replace a writer's unsaved edits.
const useInvalidateCanvasEvents = () => {
  const queryClient = useQueryClient();
  return async (canvasId: string) => {
    await queryClient.invalidateQueries({
      queryKey: [VMETA_EVENTS_KEY, canvasId],
    });
  };
};

export const useCreateVmetaEvent = (): UseMutationResult<
  EventDefinitionEdit,
  unknown,
  { canvasId: string; body: EventDefinitionInput }
> => {
  const invalidate = useInvalidateCanvasEvents();
  return useMutation({
    mutationFn: async ({ canvasId, body }) =>
      (await axios.post<EventDefinitionResponseBody>(base(canvasId), body)).data
        .data,
    onSettled: async (_data, _error, { canvasId }) => invalidate(canvasId),
  });
};

export const useUpdateVmetaEvent = (): UseMutationResult<
  EventDefinitionEdit,
  unknown,
  { canvasId: string; eventId: string; body: EventDefinitionUpdateBody }
> => {
  const invalidate = useInvalidateCanvasEvents();
  return useMutation({
    mutationFn: async ({ canvasId, eventId, body }) =>
      (
        await axios.put<EventDefinitionResponseBody>(
          `${base(canvasId)}/${segment(eventId)}`,
          body
        )
      ).data.data,
    onSettled: async (_data, _error, { canvasId }) => invalidate(canvasId),
  });
};

export const useDeleteVmetaEvent = (): UseMutationResult<
  void,
  unknown,
  { canvasId: string; eventId: string; version: string | null }
> => {
  const invalidate = useInvalidateCanvasEvents();
  return useMutation({
    mutationFn: async ({ canvasId, eventId, version }) => {
      // A null version (never edited since legacy) is sent as no parameter at all.
      await axios.delete<EventDeleteResponseBody>(
        `${base(canvasId)}/${segment(eventId)}`,
        version === null ? undefined : { params: { version } }
      );
    },
    onSettled: async (_data, _error, { canvasId }) => invalidate(canvasId),
  });
};
